import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useProvince } from '../contexts/ProvinceContext';
import { useGameTime } from '../hooks/useGameTime';
import { useDossier } from '../hooks/useDossier';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { findSiblingGroup } from '../constants/siblingGroups';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { useSortState } from '../components/ui/SortableTable';
import { MobileDossierShell } from '../components/dossier/MobileDossierShell';
import { DesktopDossierShell } from '../components/dossier/DesktopDossierShell';
import { deriveEffectFilterOptions, resolveEffectiveFilter } from '../utils/dossierEffectFilter';
import { RESOURCES_SORT_OPTIONS, MILITARY_SORT_OPTIONS } from '../utils/dossierSort';
import { COLORS } from '../constants/theme';
import type { SortState } from '../components/ui/SortableTable';
import type { EffectFilterOption } from '../utils/dossierEffectFilter';
import type { ResourcesSortValue, MilitarySortValue } from '../utils/dossierSort';
import type { DossierProvinceRow, KingdomDossierDto } from '../types/dossier';

export type TabId = 'resources' | 'effects' | 'military';

/**
 * SF-156: props for the mobile shell. Loading/error/empty render-state ownership is asymmetric by
 * design (see Application Interface's "Two shell-props interfaces" note) — mobile owns rendering
 * all three states from `dossier`/`isLoading`/`error`, desktop doesn't.
 */
export interface MobileDossierShellProps {
  dossier: KingdomDossierDto | null;
  currentTick: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  mobileSortValue: ResourcesSortValue | MilitarySortValue;
  onSortChange: (value: ResourcesSortValue | MilitarySortValue) => void;
  mobileEffectFilter: string | null;
  onEffectFilterChange: (filter: string | null) => void;
  effectFilterOptions: EffectFilterOption[];
  selectedMember: DossierProvinceRow | null;
  onSelectMember: (member: DossierProvinceRow | null) => void;
  showSiblingChrome: boolean;
}

/** SF-156: props for the desktop shell — `dossier` stays non-nullable (see Component 3). */
export interface DesktopDossierShellProps {
  dossier: KingdomDossierDto;
  currentTick: number;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  resourcesSort: SortState;
  effectsSort: SortState;
  militarySort: SortState;
  onResourcesSort: (key: string) => void;
  onEffectsSort: (key: string) => void;
  onMilitarySort: (key: string) => void;
}

/**
 * Kingdom Dossier page — real scouting/intel (Crystal Ball, Spy on Military, Survey, Infiltrate
 * reports, fuzzy values, staleness).
 *
 * Route: /world/dossier/:kingdomId?
 *
 * SF-156: gains a `useAppShellPlatform()` switch between `MobileDossierShell` and
 * `DesktopDossierShell` — all state (`activeTab`, desktop's three `useSortState()` instances,
 * mobile's independent sort/filter state, `?member=` derivation, `kingdomId` resolution/URL sync,
 * live-region announcement, `showSiblingChrome`) stays at this page level and threads down to
 * whichever shell is active (Application Interface's State ownership section).
 *
 * SF-169: the Roster tab and all in-page browsing chrome (picker/Previous/Next/Random) were
 * removed — that's now the World Viewer's job (/world/kingdoms/:kingdomId?), which reads live,
 * non-fuzzy public data instead of this page's snapshot copy. Dossier is reachable by direct
 * route (own-kingdom-default fallback, same as before) or via the reciprocal "View Dossier" /
 * "View public roster" cross-links.
 */
export function Dossier() {
  const { kingdomId: kingdomIdParam } = useParams<{ kingdomId?: string }>();
  const { province } = useProvince();
  const currentTick = useGameTime(province ?? null);
  const platform = useAppShellPlatform();
  const { pathname } = useLocation();

  // Own kingdom id (used for default view)
  const ownKingdomId = province?.metadata?.location
    ? `${province.metadata.location.island}:${province.metadata.location.kingdom}`
    : null;

  // Default to own kingdom if no URL param is provided
  const initialKingdomId = kingdomIdParam ?? ownKingdomId;
  const [kingdomId, setKingdomId] = useState<string | null>(initialKingdomId);
  // Platform-derived default (SF-156): Military on mobile, Resources on desktop, resolved once at
  // first render and NOT reset on a live breakpoint flip — the value is valid on both shells' tab
  // strips either way, only its position in the strip differs (CLAUDE.md SF-147 rule).
  const [activeTab, setActiveTab] = useState<TabId>(() => (platform === 'mobile' ? 'military' : 'resources'));
  const [announcement, setAnnouncement] = useState('');

  // Per-tab sort states (lifted to parent to survive tab switches) — desktop only, unchanged.
  const resourcesSort = useSortState();
  const effectsSort = useSortState();
  const militarySort = useSortState();

  // Mobile-only sort/filter/selection state (SF-156) — independent of desktop's useSortState()
  // instances, mirroring TargetFinderShellProps' "mobile owns its own sort state" precedent.
  const [mobileSortValue, setMobileSortValue] = useState<ResourcesSortValue | MilitarySortValue>('province-asc');
  const [mobileEffectFilterRaw, setMobileEffectFilterRaw] = useState<string | null>(null);

  const { dossier, isLoading, error, refresh } = useDossier(kingdomId);

  // effectFilterOptions is the live-derived option set (category options + one per distinct
  // active effectType), threaded down as data to both the page's own validation below and the
  // shell that renders the <select> — closes the two-independent-implementations desync risk.
  const effectFilterOptions = useMemo(
    () => (dossier ? deriveEffectFilterOptions(dossier.provinces) : []),
    [dossier]
  );
  // Effective (validated) filter value — resolves to null ("All") if the raw stored selection is
  // no longer present in the freshly-derived option set (e.g. a refresh() changed the kingdom's
  // active-effect set), rather than leaving a controlled <select> bound to a nonexistent value.
  const mobileEffectFilter = useMemo(
    () => resolveEffectiveFilter(mobileEffectFilterRaw, effectFilterOptions),
    [mobileEffectFilterRaw, effectFilterOptions]
  );

  // Reset the raw effect-filter selection on kingdom switch — Dossier.tsx doesn't unmount on a
  // kingdom-to-kingdom navigation (war chips, cross-links), so a stale selection could
  // coincidentally still match a same-named effect type in the new kingdom's option set and
  // silently carry over (CLAUDE.md SF-174 RESET_FOR_NEW_TARGET rule — keyed on the primitive
  // kingdomId, not object identity).
  useEffect(() => {
    setMobileEffectFilterRaw(null);
  }, [kingdomId]);

  // `?member=` selection — derived, not independent state, exactly like TargetFinder.tsx's
  // selectedTarget. A stale/nonexistent selection (deep link to a departed province, or a
  // kingdom switch while a member was selected) resolves to null; useDossier.ts's kingdomId-
  // change fix (SF-156) ensures `dossier` itself is null during the in-flight window of a
  // kingdom-to-kingdom switch, so this can never transiently resolve against a wrong-kingdom
  // roster.
  const [searchParams, setSearchParams] = useSearchParams();
  const memberParam = searchParams.get('member');
  const selectedMember = useMemo(
    () => dossier?.provinces.find((p) => p.provinceId === memberParam) ?? null,
    [dossier, memberParam]
  );

  function handleSelectMember(member: DossierProvinceRow | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (member) {
        next.set('member', member.provinceId);
      } else {
        next.delete('member');
      }
      return next;
    });
  }

  // Sets the active tab, resets mobile's sort selection to the new tab's default (Resources and
  // Military have disjoint option sets), and (mobile only) resets scroll to top — an in-page tab
  // switch here doesn't change the route, so without this a player scrolled deep into one tab's
  // content lands mid-scroll in the newly-active tab's content (CLAUDE.md SF-173 rule, mirrors
  // MobileStrategyShell.tsx:122's precedent). scrollTo is gated to mobile — desktop's tab strip
  // predates this spec and never scrolled on switch; only the tab/sort-reset logic is shared.
  function handleTabChange(tab: TabId) {
    setActiveTab(tab);
    setMobileSortValue(tab === 'military' ? MILITARY_SORT_OPTIONS[0].value : RESOURCES_SORT_OPTIONS[0].value);
    if (platform === 'mobile') {
      window.scrollTo(0, 0);
    }
  }

  // Sibling-chrome / floating-button interaction: computed the same way Layout.tsx does. `false`
  // whenever :kingdomId makes the *path* non-bare — not `?member=`, which never affects route
  // matching (see Application Interface's Sibling-chrome section).
  const showSiblingChrome = platform === 'mobile' && findSiblingGroup(pathname) !== undefined;

  // Sync kingdomId to URL param (or fall back to own kingdom once province loads)
  useEffect(() => {
    const effective = kingdomIdParam ?? ownKingdomId;
    if (!effective) return;
    setKingdomId(effective);
  }, [kingdomIdParam, ownKingdomId]);

  // Live region announcement for screen readers — this page can still change kingdomId via full
  // route navigation from external links (the reciprocal cross-links, or DossierHeader's own war
  // chips) without the component unmounting, so this stays even though in-page browsing chrome is
  // gone (SF-169 Decision 7: kept, not moved — KingdomViewer gets its own independent copy for
  // its own in-page nav). Skip the initial mount so a fresh page load isn't announced as if the
  // user had just navigated.
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (kingdomId) setAnnouncement(`Now viewing Kingdom ${kingdomId}`);
  }, [kingdomId]);

  return (
    <div>
      {/* Live region — announces kingdom changes for screen readers navigating via external links */}
      <div aria-live="polite" className="sr-only">{announcement}</div>

      {platform === 'mobile' ? (
        <MobileDossierShell
          dossier={dossier}
          currentTick={currentTick}
          isLoading={isLoading}
          error={error}
          refresh={refresh}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          mobileSortValue={mobileSortValue}
          onSortChange={setMobileSortValue}
          mobileEffectFilter={mobileEffectFilter}
          onEffectFilterChange={setMobileEffectFilterRaw}
          effectFilterOptions={effectFilterOptions}
          selectedMember={selectedMember}
          onSelectMember={handleSelectMember}
          showSiblingChrome={showSiblingChrome}
        />
      ) : (
        <>
          {/* Header controls */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${COLORS.border}`,
              background: COLORS.card,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {/* Refresh button — controls only the primary dossier fetch */}
            {kingdomId && (
              <button
                onClick={refresh}
                disabled={isLoading}
                style={{
                  padding: '4px 12px',
                  background: COLORS.card,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.text.tertiary,
                  cursor: isLoading ? 'wait' : 'pointer',
                  fontSize: 13,
                }}
              >
                {isLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
          </div>

          {!kingdomId && (
            <div style={{ padding: 32 }}>
              <Spinner />
            </div>
          )}

          {kingdomId && isLoading && !dossier && (
            <div style={{ padding: 32 }}>
              <Spinner />
            </div>
          )}

          {error && <ErrorMessage message={error} />}

          {dossier && (
            <DesktopDossierShell
              dossier={dossier}
              currentTick={currentTick}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              resourcesSort={resourcesSort.sortState}
              effectsSort={effectsSort.sortState}
              militarySort={militarySort.sortState}
              onResourcesSort={resourcesSort.handleSort}
              onEffectsSort={effectsSort.handleSort}
              onMilitarySort={militarySort.handleSort}
            />
          )}
        </>
      )}
    </div>
  );
}
